// ─── What a dashboard's filters actually resolve to ───────────────────────────
// Pure, no React. THE one rule for "which value is each filter set to", used by
// the dashboard view and mirrored on the server (server/query.js
// dashboardFilterValues) for anything that renders a tile outside the dashboard.
//
// It lives here because it had drifted into two implementations that disagreed,
// and the place you SAW the disagreement was a pinned tile on the home page
// showing a different number from the same tile on its dashboard (reported
// 2026-08-27: 237 pinned vs 4,163 live). Five separate rules existed on the
// dashboard and nowhere else:
//
//   1. keepImportedFilters / folderKeepImported — the Looker-imported
//      default_value is authoritative and suite locks, client defaults and saved
//      views are ALL ignored. The pinned copy applied the lock anyway, so it read
//      a fraction of the real number.
//   2. per-dashboard lock overrides (suite.dashboardLocks[id]) on top of the
//      suite-wide locks — the pinned copy only had the suite-wide ones.
//   3. an EMPTY lock entry CLEARS the filter (so a template's baked default can't
//      leak through) while leaving it editable — the pinned copy ignored empties
//      and let the baked default through.
//   4. the saved-view overlay is entity-default THEN the user's own view — the
//      pinned copy only ever read the entity default.
//   5. a shared link's ?f= overrides last, for non-locked filters.
//
// Anything that renders a dashboard tile has to apply all five or it is showing a
// different query under the same title. test/dashboardFilters.test.js runs this
// and the server mirror over the same cases and fails if they disagree.
//
// NOT covered here, because it needs a live query rather than a rule: the
// days-to-go sync in APPLY mode (ViewPage.applyDaysToGo / server
// daysBeforeOverlayFor) reads a number off a source tile and sets a filter from
// it. Callers that can afford the read apply it as a further overlay.

// → { vals: { [filterName]: value }, lockedMap: { [filterName]: true } }
export function dashboardFilterValues({ def, suite, dashboardId, overlay, shared } = {}) {
  const data = def || {};
  // "Keep imported filters": the dashboard's own default_value is authoritative.
  // Nothing else applies — not locks, not the client default, not a saved view.
  if (data.keepImportedFilters || data.folderKeepImported) {
    const vals = {};
    for (const f of data.filters || []) vals[f.name] = f.default_value || '';
    return { vals, lockedMap: {} };
  }
  // Suite-wide locks, then this dashboard's own overrides on top (most specific
  // wins) — an admin can lock one dashboard for a client without the whole suite.
  const lockMap = { ...((suite && suite.lockedFilters) || {}), ...((suite && suite.dashboardLocks && suite.dashboardLocks[dashboardId]) || {}) };
  const norm = {};
  for (const [k, v] of Object.entries(lockMap)) norm[String(k).trim().toLowerCase()] = v;
  const vals = {};
  const lockedMap = {};
  for (const f of data.filters || []) {
    vals[f.name] = f.default_value || '';
    const field = String(f.field || f.dimension || '').trim().toLowerCase();
    const nameKey = String(f.name || '').trim().toLowerCase();
    const v = norm[nameKey] != null ? norm[nameKey] : (field ? norm[field] : undefined);
    // A lock/default entry wins over the dashboard's own default_value. An
    // explicitly EMPTY entry CLEARS the filter (so the template's baked default
    // doesn't leak through) but leaves it editable; a non-empty value also locks it.
    if (v != null) { vals[f.name] = v; if (v !== '') lockedMap[f.name] = true; }
  }
  const apply = (o) => { if (o) for (const [k, v] of Object.entries(o)) { if (k in vals && !lockedMap[k] && typeof v === 'string') vals[k] = v; } };
  apply(overlay); // entity default, then the user's own view (merge before calling)
  apply(shared);  // a shared link's ?f= wins over both, still never over a lock
  return { vals, lockedMap };
}
